import { useState, useCallback, useEffect } from "react";
import { useDashboardData } from "../hooks/useDashboardData";
import { useDashboardAccessGuard } from "../hooks/useDashboardAccessGuard";
import { fetchAdminOrgAll, fetchProjects } from "../services/dashboardApi";
import {
  ORG_ID,
  HBNEXT_ORG_ID,
  AI_DASHBOARD_PRIVILEGES,
} from "../../../constant/constant";
import { checkPrivileges } from "../../../utils/rolesHelper";
import ShareLinkPanel from "../components/ShareLinkPanel";
import DashboardClientFilter from "../components/DashboardClientFilter";
import DashboardShell from "../components/DashboardShell";
import OverviewSection from "../components/sections/OverviewSection";
import InspectionsSection from "../components/sections/InspectionsSection";
import IncidentsSection from "../components/sections/IncidentsSection";
import CredentialsSection from "../components/sections/CredentialsSection";
import AssetsSection from "../components/sections/AssetsSection";
import BenchmarksSection from "../components/sections/BenchmarksSection";
import UsersSection from "../components/sections/UsersSection";
import SubcontractorsSection from "../components/sections/SubcontractorsSection";
import Loader from "../../../library/common/Loader";
import "../reportDashboard.scss";

const FIVE_MINUTES = 5 * 60 * 1000;

const ReportDashboardPage = () => {
  const activeOrgId = Number(ORG_ID()) || null;

  // Route-level gate. The AI-Dashboard tab in reportAnalytics.jsx is hidden from
  // users without a dashboard privilege, but the route is reachable by typed URL or
  // stale bookmark, and every request this page makes would 401. Match the page's
  // own access-lost contract rather than rendering an empty shell.
  const canViewDashboard = checkPrivileges(AI_DASHBOARD_PRIVILEGES);

  // Client search is a narrower gate than viewing the dashboard: it is offered only
  // to HB NEXT staff sitting in HB NEXT's own org. Same rule as the stormwater
  // Inspection Record page's "HBView" — switch to another org via the header and this
  // flips false, so the picker disappears and the dashboard is scoped to that org.
  // Privilege 12 only, not 15/17, and deliberately not the REPORT & ANALYTICS triad:
  // picking another client makes GetOrgAll calls for an org that is not the caller's
  // own, which DashboardAdminController.IsOrgAccessAllowed permits for privilege 12
  // only. Widening this to 15 (SWITCH ORGANIZATION), 17 (GLOBAL USER) or 114/115/116
  // would show a picker whose selections then fail server-side.
  const canSearchClients =
    parseInt(ORG_ID(), 10) === parseInt(HBNEXT_ORG_ID, 10) && checkPrivileges([12]);

  const [selectedClient, setSelectedClient] = useState(null);
  const orgId = selectedClient?.key ? Number(selectedClient.key) : activeOrgId;
  const [dateRange, setDateRange] = useState({ preset: "all", from: null, to: null });

  const fetchOrgAll = useCallback(() => {
    return orgId && canViewDashboard
      ? fetchAdminOrgAll(orgId, { from: dateRange.from, to: dateRange.to })
      : Promise.resolve(null);
  }, [orgId, canViewDashboard, dateRange.from, dateRange.to]);

  const { data, loading, refetching, error, notFound } = useDashboardData(
    fetchOrgAll,
    [orgId, dateRange.from, dateRange.to],
    { silentRefetchMs: FIVE_MINUTES }
  );

  // dashboardApi calls axios directly rather than going through
  // services/axiosRequest.js, so a lapsed session on this page never hit that
  // module's 401 handling and just left stale org data on screen. Match the
  // app-wide contract (axiosRequest.js sends a rejected session to
  // /access-denied) instead of inventing a second behaviour for this page.
  const handleAccessLost = useCallback(() => {
    window.location.href = "/access-denied";
  }, []);
  useDashboardAccessGuard(handleAccessLost);

  // Send an unprivileged caller away instead of rendering a shell whose every
  // request 401s. In an effect, not an early return, so the hooks above always run.
  useEffect(() => {
    if (!canViewDashboard) {
      handleAccessLost();
    }
  }, [canViewDashboard, handleAccessLost]);

  // Liveness probe on section switch — Overview, Benchmarks and Analytics
  // render straight from the payload fetched at page load and issue no requests
  // of their own. One row of the cheapest list endpoint, gated on the same
  // SafetyDashboardGetOrganisationDashboard privilege as everything else here.
  const validateAccess = useCallback(() => {
    if (!orgId) return;
    fetchProjects({ mode: "admin", orgId }, { page: 1, pageSize: 1 }).catch(() => {});
  }, [orgId]);

  const apiCtx = {
    mode: "admin",
    orgId,
    from: dateRange.from,
    to: dateRange.to,
  };

  if (!canViewDashboard) return null;

  return (
    <div className="rdPage">
      {canSearchClients && (
        <DashboardClientFilter value={selectedClient} onChange={setSelectedClient} />
      )}

      {!orgId && (
        <div className="rdMessage">
          No organization selected. Use the organization search in the header to pick one.
        </div>
      )}

      {orgId && (loading || refetching) && !data && <Loader />}

      {orgId && !data && (error || notFound) && (
        <div className="rdMessage rdMessage--error">
          {notFound
            ? "No dashboard data found for this organization."
            : "Something went wrong loading this organization's dashboard."}
        </div>
      )}

      {orgId && data && (
        <>
          <ShareLinkPanel
            organisationId={orgId}
            clientName={data.client.clientName}
            accentColor={data.client.accentColor}
            activeShareToken={data.client.activeShareToken}
            shareTokenExpiresAt={data.client.shareTokenExpiresAt}
          />
          <DashboardShell
            clientName={data.client.clientName}
            accentColor={data.client.accentColor}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            refetching={refetching}
            onSectionChange={validateAccess}
            sections={[
              <OverviewSection key="overview" data={data} apiCtx={apiCtx} refetching={refetching} />,
              <InspectionsSection key="inspections" data={data} apiCtx={apiCtx} refetching={refetching} />,
              <IncidentsSection key="incidents" data={data} apiCtx={apiCtx} refetching={refetching} />,
              <CredentialsSection key="credentials" data={data} apiCtx={apiCtx} refetching={refetching} />,
              <AssetsSection key="assets" data={data} apiCtx={apiCtx} refetching={refetching} />,
              <BenchmarksSection key="benchmarks" data={data} dateRange={dateRange} />,
              <UsersSection key="users" data={data} apiCtx={apiCtx} />,
              <SubcontractorsSection key="subcontractors" data={data} apiCtx={apiCtx} refetching={refetching} />,
            ]}
          />
        </>
      )}
    </div>
  );
};

export default ReportDashboardPage;
